import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/Home.css";

function Home() {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [activeGallery, setActiveGallery] = useState(0);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [contactStatus, setContactStatus] = useState("");
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const [activeProgram, setActiveProgram] = useState(null);
  const galleryTrackRef = useRef(null);
  const testimonialTrackRef = useRef(null);
  const dragStateRef = useRef({
    isDragging: false,
    startX: 0,
    startScrollLeft: 0,
    ref: null,
  });

  const heroStats = useMemo(
    () => [
      { label: "World Records", value: "100+" },
      { label: "Record Holders", value: "45+" },
      { label: "Established", value: "2018" },
    ],
    []
  );

  const programs = useMemo(
    () => [
      {
        title: "One-on-One Hula Hoop",
        description: "For kids and women.",
        details:
          "Personalized online coaching with individual pacing and direct correction.",
        mode: "Online",
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="7" />
          </svg>
        ),
      },
      {
        title: "Group Session Hula Hoop",
        description: "For kids and women.",
        details:
          "Structured online group classes focused on rhythm, stamina, and teamwork.",
        mode: "Online",
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M5 17l6-10 4 7 4-6" />
            <circle cx="5" cy="17" r="1.5" />
            <circle cx="11" cy="7" r="1.5" />
            <circle cx="15" cy="14" r="1.5" />
            <circle cx="19" cy="8" r="1.5" />
          </svg>
        ),
      },
      {
        title: "Rhythmic Gymnastics",
        description: "For girls.",
        details:
          "Offline coaching for flexibility, movement quality, and apparatus control.",
        mode: "Offline",
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M7 18c4-8 6-12 10-12" />
            <path d="M6 8c3 0 5 2 5 5" />
          </svg>
        ),
      },
      {
        title: "Hula Hoop",
        description: "For boys.",
        details:
          "Offline skill development with form correction, endurance drills, and progression milestones.",
        mode: "Offline",
        icon: (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 4l2.2 4.5 5 .7-3.6 3.5.9 4.9-4.5-2.4-4.5 2.4.9-4.9L4.8 9.2l5-.7z" />
          </svg>
        ),
      },
    ],
    []
  );

  const galleryImages = useMemo(
    () => [
      "gallery/1.jpg",
      "gallery/2.jpg",
      "gallery/3.jpg",
      "gallery/4.jpg",
      "gallery/5.png",
      "gallery/6.jpg",
      "gallery/7.jpg",
      "gallery/8.jpeg",
      "gallery/9.jpeg",
      "gallery/10.jpeg",
    ],
    []
  );

  const testimonials = useMemo(
    () => [
      {
        quote:
          "Under the unwavering support and guidance of Coach Vijayalakshmi mam, my daughter participated in the 69th National School Games Federation of India, held at Kolkata from January 19, 2026, to January 20, 2026. This was a remarkable experience for her, and representing Tamil Nadu State is a huge honor.",
        name: "Parent of Shakshitha",
        role: "Parent",
      },
      {
        quote:
          "Excellent experience with this class, especially for online hula-hoop training. Coach Vijaylaxmi Ma’am has an amazing ability to judge each child’s potential and provide personalised coaching. Her dedication, patience, and extremely positive attitude help children learn with confidence and joy. Highly recommended",
        name: "Parent of Ira Joshi",
        role: "Parent",
      },
      {
        quote:
          "My daughter is under her coaching for a couple of years. A best place to learn gymnastics. She is a very encouraging and positive person. Handles all the kids with a smiling face all time. My kid is blessed to be her student.",
        name: "Parent of Praharshitha",
        role: "Parent",
      },
      {
        quote:
          "My daughter was learning hoop from last couple of years. The coach Mrs. Vijiyalakshmi was really helpful and her teaching was very good I highly recommend this for any Hulahoop enthusiast to join",
        name: "Parent of Vaishnavi Shree",
        role: "Parent",
      },
    ],
    []
  );

  useEffect(() => {
    const items = document.querySelectorAll(".reveal");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.12 }
    );

    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const galleryEl = galleryTrackRef.current;
    const testimonialEl = testimonialTrackRef.current;

    if (!galleryEl || !testimonialEl) {
      return undefined;
    }

    const updateActiveFromScroll = (element, setActive, totalItems) => {
      const card = element.querySelector(".carousel-card");
      if (!card) {
        return;
      }
      const cardWidth = card.getBoundingClientRect().width;
      const styles = window.getComputedStyle(element);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || "0");
      const step = cardWidth + gap;
      if (step <= 0) {
        return;
      }

      const nextIndex = Math.max(0, Math.min(totalItems - 1, Math.round(element.scrollLeft / step)));
      setActive(nextIndex);
    };

    const onGalleryScroll = () =>
      updateActiveFromScroll(galleryEl, setActiveGallery, galleryImages.length);
    const onTestimonialScroll = () =>
      updateActiveFromScroll(testimonialEl, setActiveTestimonial, testimonials.length);

    galleryEl.addEventListener("scroll", onGalleryScroll, { passive: true });
    testimonialEl.addEventListener("scroll", onTestimonialScroll, { passive: true });

    return () => {
      galleryEl.removeEventListener("scroll", onGalleryScroll);
      testimonialEl.removeEventListener("scroll", onTestimonialScroll);
    };
  }, [galleryImages.length, testimonials.length]);

  const scrollCarouselTo = (ref, index) => {
    const element = ref.current;
    if (!element) {
      return;
    }
    const card = element.querySelector(".carousel-card");
    if (!card) {
      return;
    }

    const cardWidth = card.getBoundingClientRect().width;
    const styles = window.getComputedStyle(element);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || "0");
    element.scrollTo({
      left: index * (cardWidth + gap),
      behavior: "smooth",
    });
  };

  const handleCarouselKeyDown = (event, ref, activeIndex, setActiveIndex, total) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
      return;
    }
    event.preventDefault();
    const delta = event.key === "ArrowRight" ? 1 : -1;
    const nextIndex = Math.max(0, Math.min(total - 1, activeIndex + delta));
    setActiveIndex(nextIndex);
    scrollCarouselTo(ref, nextIndex);
  };

  const startCarouselDrag = (event, ref) => {
    if (event.pointerType === "mouse" && event.button !== 0) {
      return;
    }
    const element = ref.current;
    if (!element) {
      return;
    }

    dragStateRef.current = {
      isDragging: true,
      startX: event.clientX,
      startScrollLeft: element.scrollLeft,
      ref,
    };

    if (element.setPointerCapture) {
      element.setPointerCapture(event.pointerId);
    }
  };

  const moveCarouselDrag = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState.isDragging || !dragState.ref?.current) {
      return;
    }
    const delta = event.clientX - dragState.startX;
    dragState.ref.current.scrollLeft = dragState.startScrollLeft - delta;
  };

  const endCarouselDrag = (event) => {
    const dragState = dragStateRef.current;
    if (!dragState.isDragging || !dragState.ref?.current) {
      return;
    }
    dragState.isDragging = false;
    if (dragState.ref.current.releasePointerCapture) {
      try {
        dragState.ref.current.releasePointerCapture(event.pointerId);
      } catch (error) {
        // Ignore if pointer capture is not active.
      }
    }
  };

  const handleContactChange = (event) => {
    const { name, value } = event.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setIsSubmittingContact(true);
    setContactStatus("");

    try {
      if (!process.env.REACT_APP_API_URL) {
        throw new Error("Missing API URL");
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/contact/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || "Failed to send message");
      }

      setContactStatus("Message sent successfully.");
      setContactForm({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      setContactStatus("Could not send message right now. Please call or email us.");
    } finally {
      setIsSubmittingContact(false);
    }
  };

  return (
    <main className="material-page">
      <section className="hero" id="hero">
        <div className="container material-grid hero-grid reveal">
          <div className="hero-copy">
            <p className="eyebrow">CHENNAI HOOPERS</p>
            <h1>Train with Discipline. Achieve with Confidence.</h1>
            <p>
              Professional hula hoop and rhythmic gymnastics training in Chennai since 2018.
            </p>
            <div className="actions">
              <a href="#programs" className="btn-outline">View Programs</a>
            </div>
            <div className="stat-chips" role="list" aria-label="Academy highlights">
              {heroStats.map((stat) => (
                <article key={stat.label} className="stat-chip" role="listitem">
                  <p>{stat.value}</p>
                  <small>{stat.label}</small>
                </article>
              ))}
            </div>
          </div>
          <div className="hero-image-card">
            <img src="hero_middle.jpg" alt="Chennai Hoopers training session" className="hero-image" />
          </div>
        </div>
      </section>

      <section id="about" className="section section-surface divider-top">
        <div className="container material-grid about-grid reveal">
          <div className="about-copy material-block">
            <p className="eyebrow">ABOUT CHENNAI HOOPERS</p>
            <h2>Structured Coaching Since 2018</h2>
            <p>
              Founded in January 2018 and led by V. Vijayalakshmi, Chennai Hoopers is built on a disciplined training model that develops skill, coordination, rhythm, and performance confidence.
            </p>
            <p>
              The academy supports learners across levels with focused progression pathways in hula hoop and rhythmic gymnastics.
            </p>
          </div>
          <div className="about-image-wrap material-block">
            <img src="about_us1.png" alt="Training at Chennai Hoopers" className="about-image" />
          </div>
        </div>
      </section>

      <section id="programs" className="section section-alt divider-top">
        <div className="container reveal">
          <p className="eyebrow">PROGRAMS</p>
          <h2>Professional Training Tracks</h2>
          <div className="program-grid">
            {programs.map((program) => (
              <article key={program.title} className="program-card material-elevation-1">
                <span className="program-icon" aria-hidden="true">{program.icon}</span>
                <h3>{program.title}</h3>
                <span
                  className={
                    program.mode === "Online" ? "program-tag program-tag-online" : "program-tag program-tag-offline"
                  }
                >
                  {program.mode}
                </span>
                <p>{program.description}</p>
                {activeProgram === program.title && (
                  <div className="program-expanded">
                    <p className="program-details">{program.details}</p>
                    {program.mode === "Online" ? (
                      <Link to="/slots" className="program-action-btn program-action-online">
                        Register Now
                      </Link>
                    ) : (
                      <a href="#contact" className="program-action-btn program-action-offline">
                        Contact Now
                      </a>
                    )}
                  </div>
                )}
                <button
                  type="button"
                  className="learn-more-btn"
                  onClick={() =>
                    setActiveProgram((prev) => (prev === program.title ? null : program.title))
                  }
                  aria-expanded={activeProgram === program.title}
                >
                  Learn More
                  <span className={activeProgram === program.title ? "chevron open" : "chevron"} aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M7 10l5 5 5-5" />
                    </svg>
                  </span>
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="gallery" className="section section-alt divider-top">
        <div className="container reveal">
          <p className="eyebrow">GALLERY</p>
          <h2>Training and Performance Moments</h2>
          <div
            className="carousel-track gallery-carousel"
            ref={galleryTrackRef}
            role="region"
            aria-label="Gallery carousel"
            tabIndex={0}
            onKeyDown={(event) =>
              handleCarouselKeyDown(event, galleryTrackRef, activeGallery, setActiveGallery, galleryImages.length)
            }
            onPointerDown={(event) => startCarouselDrag(event, galleryTrackRef)}
            onPointerMove={moveCarouselDrag}
            onPointerUp={endCarouselDrag}
            onPointerCancel={endCarouselDrag}
          >
            {galleryImages.map((image, index) => (
              <article key={image} className="gallery-post carousel-card material-elevation-1">
                <button
                  type="button"
                  className="gallery-item"
                  onClick={() => setLightboxIndex(index)}
                  aria-label={`Open gallery image ${index + 1}`}
                >
                  <img src={image} alt={`Gallery ${index + 1}`} loading="lazy" />
                  <div className="gallery-meta">
                    {/* TODO: Add Meta later if need */}
                  </div>
                </button>
              </article>
            ))}
          </div>
          <div className="carousel-dots" role="tablist" aria-label="Gallery slide navigation">
            {galleryImages.map((image, index) => (
              <button
                key={`${image}-dot`}
                type="button"
                className={activeGallery === index ? "carousel-dot active" : "carousel-dot"}
                onClick={() => {
                  setActiveGallery(index);
                  scrollCarouselTo(galleryTrackRef, index);
                }}
                aria-label={`Go to gallery item ${index + 1}`}
                aria-selected={activeGallery === index}
                role="tab"
              />
            ))}
          </div>
        </div>
      </section>

      <section className="section section-surface divider-top">
        <div className="container reveal">
          <p className="eyebrow">TESTIMONIALS</p>
          <h2>What Families and Students Say</h2>
          <div
            className="carousel-track testimonial-carousel"
            ref={testimonialTrackRef}
            role="region"
            aria-label="Testimonials carousel"
            aria-live="polite"
            tabIndex={0}
            onKeyDown={(event) =>
              handleCarouselKeyDown(
                event,
                testimonialTrackRef,
                activeTestimonial,
                setActiveTestimonial,
                testimonials.length
              )
            }
            onPointerDown={(event) => startCarouselDrag(event, testimonialTrackRef)}
            onPointerMove={moveCarouselDrag}
            onPointerUp={endCarouselDrag}
            onPointerCancel={endCarouselDrag}
          >
            {testimonials.map((item) => (
              <article key={item.name} className="testimonial-card carousel-card material-elevation-1">
                <blockquote>{item.quote}</blockquote>
                <p>{item.name}</p>
                <small>{item.role}</small>
              </article>
            ))}
          </div>
          <div className="carousel-dots testimonial-dots" role="tablist" aria-label="Testimonial navigation">
            {testimonials.map((item, index) => (
              <button
                key={item.name}
                type="button"
                className={activeTestimonial === index ? "carousel-dot testimonial-dot active" : "carousel-dot testimonial-dot"}
                onClick={() => {
                  setActiveTestimonial(index);
                  scrollCarouselTo(testimonialTrackRef, index);
                }}
                aria-label={`Show testimonial ${index + 1}`}
                aria-selected={activeTestimonial === index}
                role="tab"
              />
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="section cta-section divider-top">
        <div className="container reveal">
          <div className="cta-grid">
            <div className="cta-copy">
              <h2>Begin Your Training Journey</h2>
              <p>Join classes that combine discipline, rhythm, and measurable progress.</p>
              <div className="actions">
                <a href="mailto:chennai.hoopers@gmail.com" className="cta-text-link">chennai.hoopers@gmail.com</a>
              </div>
              <article className="location-card">
                <h3>Location</h3>
                <p>Chennai Hoopers Academy</p>
                <small>Chennai, Tamil Nadu, India</small>
                <a
                  href="https://www.google.com/maps/place/Chennai+Hoopers/@13.0921823,80.2423362,17z/data=!3m1!4b1!4m6!3m5!1s0x3a526558e4458a05:0x8ac741fe9f8426ff!8m2!3d13.0921771!4d80.2449111!16s%2Fg%2F11fk03p1zt?entry=ttu&g_ep=EgoyMDI2MDIyNS4wIKXMDSoASAFQAw%3D%3D"
                  target="_blank"
                  rel="noreferrer"
                  className="cta-map-link"
                >
                  Open in Google Maps
                </a>
              </article>
            </div>
            <form className="journey-form" onSubmit={handleContactSubmit}>
              <h3>Contact Us</h3>
              <label htmlFor="journey-name">Full Name</label>
              <input
                id="journey-name"
                name="name"
                type="text"
                value={contactForm.name}
                onChange={handleContactChange}
                required
              />
              <label htmlFor="journey-email">Email</label>
              <input
                id="journey-email"
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleContactChange}
                required
              />
              <label htmlFor="journey-phone">Phone</label>
              <input
                id="journey-phone"
                name="phone"
                type="tel"
                value={contactForm.phone}
                onChange={handleContactChange}
                required
              />
              <label htmlFor="journey-message">Message</label>
              <textarea
                id="journey-message"
                name="message"
                rows="4"
                value={contactForm.message}
                onChange={handleContactChange}
                required
              />
              <button type="submit" className="cta-surface-button" disabled={isSubmittingContact}>
                {isSubmittingContact ? "Sending..." : "Send Message"}
              </button>
              {contactStatus && <p className="form-status">{contactStatus}</p>}
            </form>
          </div>
        </div>
      </section>

      <footer className="footer section-alt divider-top">
        <div className="container footer-grid">
          <div>
            <p>Chennai Hoopers</p>
            <small>Chennai, Tamil Nadu</small>
          </div>
          <div className="footer-links">
            <a href="mailto:chennai.hoopers@gmail.com">chennai.hoopers@gmail.com</a>
            <a href="tel:+919677590921">+91 96775 90921</a>
            <a href="https://www.instagram.com/chennaihoopers" target="_blank" rel="noreferrer">Instagram</a>
            <span>Chennai Hoopers®</span>
          </div>
        </div>
      </footer>

      {lightboxIndex !== null && (
        <div className="lightbox" role="dialog" aria-modal="true">
          <button type="button" className="lightbox-close" onClick={() => setLightboxIndex(null)}>
            Close
          </button>
          <img src={galleryImages[lightboxIndex]} alt={`Expanded gallery ${lightboxIndex + 1}`} />
        </div>
      )}
    </main>
  );
}

export default Home;
